// Wanna know what to render in the Leaderboard?
// This'll tell you!
// ---------------------------------------------------------------------------
//                  ___________________________________
//                 |   __    __    ___  _____   __     |
//                 |  / _\  / /   /___\/__   \ / _\    |
//                 |  \ \  / /   //  //  / /\ \\ \  25c|
//                 |  _\ \/ /___/ \_//  / /  \/_\ \ [ ]|
//                 |  \__/\____/\___/   \/     \__/    | __
//                 | ===_______===_______===_______=== |(__)
//                 | ===_______===_______===_______=== | ||
//                 | |*|\_     |*|  ___  |*|\_     |*| | ||
//                 | |*||__ _  |*| |_  | |*||__ _  |*| | ||
//                 | |*| \_(_) |*|  / /  |*| \_(_) |*| | ||
//                 | |*| (_)   |*| /_/   |*| (_)   |*| | ||
//                 | |*|_______|*|_______|*|_______|*| |_//
//                 | ===_______===_______===_______=== |_/
//                 | lc=___________________________=== |
//                 |   /___________________________\   |
//                 |    |                         |    |
//                _|     \_______________________/     |_
//               (_______________________________________)
//
// ---------------------------------------------------------------------------

import localStorage from "shared/util/localStorage";

export const BANNER_TYPE = {
	BillingWoes: "BillingWoes",
	Incognito: "Incognito",
	Promos: "Promos",
	IntoYou: "IntoYou",
	SupportOkC: "SupportOkC",
	Ad: "Ad",
};

const SLOT_MACHINE_GATEKEEPERS = {
	INTOYOU_MASTHEAD: "INTOYOU_MASTHEAD",
	BILLING_WOES: "BILLING_WOES",
	ALIST_DISCOUNT_MASTHEAD: "ALIST_DISCOUNT_MASTHEAD",
};

const _KEY_PREFIX = "BANNER_";
const _SEEN_COUNT_KEY_SUFFIX = "_SEEN_COUNT";
const _LAST_SEEN_KEY_SUFFIX = "_LAST_SEEN";

class SlotMachine {
	// Order in which to priotitize showing banners
	// -----------------------------------------------------------------------------
	static _bannerPriority = [
		BANNER_TYPE.BillingWoes,
		BANNER_TYPE.Incognito,
		BANNER_TYPE.Promos,
		BANNER_TYPE.IntoYou,
		BANNER_TYPE.SupportOkC,
		BANNER_TYPE.Ad,
	];

	// Conditions under which to show each banner
	// -----------------------------------------------------------------------------
	static _shouldShow = {
		[BANNER_TYPE.BillingWoes]() {
			return (
				SlotMachine._checkGatekeeper(SLOT_MACHINE_GATEKEEPERS.BILLING_WOES)
			);
		},

		[BANNER_TYPE.Incognito]() {
			const ONE_DAY = 1000 * 60 * 60 * 24 * 1;

			return (
				SlotMachine._checkIsOnHomePage() &&
				SlotMachine._checkAccountAge({ minAge: ONE_DAY }) &&
				!SlotMachine._checkHasIncognito() &&
				!SlotMachine._checkHasSeenMoreThanTimes(BANNER_TYPE.Incognito, 2) &&
				!SlotMachine._checkHasSeenRecently(BANNER_TYPE.Incognito)
			);
		},

		[BANNER_TYPE.Promos]() {
			return (
				!SlotMachine._checkIsAlist() &&
				!SlotMachine._checkHasSeenRecently(BANNER_TYPE.Promos) &&
				SlotMachine._checkGatekeeper(SLOT_MACHINE_GATEKEEPERS.ALIST_DISCOUNT_MASTHEAD)
			);
		},

		[BANNER_TYPE.IntoYou]() {
			return (
				SlotMachine._checkGatekeeper(SLOT_MACHINE_GATEKEEPERS.INTOYOU_MASTHEAD)
			);
		},

		[BANNER_TYPE.SupportOkC]() {
			const ONE_WEEK = 1000 * 60 * 60 * 24 * 7;
			const THREE_WEEKS = 1000 * 60 * 60 * 24 * 21;

			return (
				SlotMachine._checkIsOnHomePage() &&
				SlotMachine._checkAccountAge({ minAge: ONE_WEEK, maxAge: THREE_WEEKS }) &&
				!SlotMachine._checkIsAlist() &&
				!SlotMachine._checkHasSeenRecently(BANNER_TYPE.SupportOkC)
			);
		},

		[BANNER_TYPE.Ad]() {
			return (
				!SlotMachine._checkIsAdFree()
			);
		},
	};

	// LocalStorage getters/setters
	// -----------------------------------------------------------------------------

	static _getBannerSeenCountKey(bannerType) {
		return _KEY_PREFIX + bannerType + _SEEN_COUNT_KEY_SUFFIX;
	}

	static _getBannerSeenCount(bannerType) {
		const bannerSeenCountKey = SlotMachine._getBannerSeenCountKey(bannerType);
		return parseInt(localStorage.getItem(bannerSeenCountKey) || 0, 10);
	}

	static _setBannerSeenCount(bannerType, count) {
		const bannerSeenCountKey = SlotMachine._getBannerSeenCountKey(bannerType);
		localStorage.setItem(bannerSeenCountKey, count);
	}

	static _getBannerLastSeenKey(bannerType) {
		return _KEY_PREFIX + bannerType + _LAST_SEEN_KEY_SUFFIX;
	}

	static _getBannerLastSeen(bannerType) {
		const bannerLastSeenKey = SlotMachine._getBannerLastSeenKey(bannerType);
		return parseInt(localStorage.getItem(bannerLastSeenKey), 10);
	}

	static _setBannerLastSeen(bannerType, lastSeen) {
		const bannerLastSeenKey = SlotMachine._getBannerLastSeenKey(bannerType);
		localStorage.setItem(bannerLastSeenKey, lastSeen);
	}

	// Available SlotMachine checks
	// -----------------------------------------------------------------------------

	/**
	 * Check if banner has been seen more than X times.
	 * @param {string} bannerType - Type of banner to check.
	 * @param {number} times - Number of times it should have been seen more than.
	 * @returns {bool}
	 */
	static _checkHasSeenMoreThanTimes(bannerType, times) {
		return SlotMachine._getBannerSeenCount(bannerType) > times;
	}

	/**
	 * Check if banner has been seen within the last hour.
	 * @param {string} bannerType - Type of banner to check.
	 * @returns {bool}
	 */
	static _checkHasSeenRecently(bannerType) {
		const ONE_HOUR = 60 * 60 * 1000;
		const now = Date.now();

		// It was seen recently if we've ever seen it,
		// and the last time was within the last hour.
		const lastSeen = SlotMachine._getBannerLastSeen(bannerType);
		return lastSeen && lastSeen > now - ONE_HOUR;
	}

	/**
	 * Check if user account age meets min/max requirements.
	 * @param {object} requirements - Account age requirements.
	 * @param {number|undefined} requirements.minAge - Minimum age to meet.
	 * @param {number|undefined} requirements.maxAge - Maximum age to meet.
	 * @returns {bool}
	 */
	static _checkAccountAge({ minAge, maxAge }) {
		const joinDate = SlotMachine._context.joinDate;
		const now = Date.now();
		const accountAge = now - joinDate;

		// Exit with false if account is under min age req.
		if (minAge !== undefined && accountAge < minAge) {
			return false;
		}

		// Exit with false if account is over max age req.
		if (maxAge !== undefined && accountAge > maxAge) {
			return false;
		}

		// We good.
		return true;
	}

	/**
	 * Check a Gatekeeper.
	 * @param {string} checkName - name of the gatekeeper to check.
	 * @returns {bool}
	 */
	static _checkGatekeeper(checkName) {
		return SlotMachine._context.gatekeeperChecks[checkName] === true;
	}

	/**
	 * Check if the user has Alist.
	 * @returns {bool}
	 */
	static _checkIsAlist() {
		return SlotMachine._context.isAlist === true;
	}

	/**
	 * Check if the user has no ads.
	 * @returns {bool}
	 */
	static _checkIsAdFree() {
		return SlotMachine._context.isAdFree === true;
	}

	/**
	 * Check if the user has Incognito.
	 * @returns {bool}
	 */
	static _checkHasIncognito() {
		return SlotMachine._context.hasIncognito === true;
	}

	/**
	 * Check if the user is on the homepage.
	 * @returns {bool}
	 */
	static _checkIsOnHomePage() {
		return SlotMachine._context.isOnHomepage === true;
	}

	/**
	 * Ensure we have all of our required parameters.
	 * @param {object} context - Information required by SlotMachine to decide what to show.
	 */
	static _validateContext(context) {
		const hasRequiredParams = (
			typeof context.isOnHomepage === "boolean" &&
			typeof context.isAlist === "boolean" &&
			typeof context.isAdFree === "boolean" &&
			typeof context.hasIncognito === "boolean" &&
			typeof context.joinDate === "number" &&
			typeof context.gatekeeperChecks === "object"
		);
		const hasAllGatekeeperChecks = hasRequiredParams &&
			Object.values(SLOT_MACHINE_GATEKEEPERS)
				.every((checkName) => typeof context.gatekeeperChecks[checkName] === "boolean");

		// Throw if something is wrong.
		if (!hasRequiredParams || !hasAllGatekeeperChecks) {
			throw new Error(`Invalid SlotMachine Context: ${!hasRequiredParams
				? "Missing required parameters."
				: "Missing required gatekeeper checks."
			}`);
		}
	}

	// SlotMachine Public API
	// -----------------------------------------------------------------------------

	/**
	 * Get the type of banner we should show, according to SlotMachine.
	 * @param {object} context - Information required by SlotMachine to decide what to show.
	 * @param {boolean} context.isOnHomepage - Is the user on the homepage right now?
	 * @param {boolean} context.isAlist - Does the user have Alist?
	 * @param {boolean} context.isAdFree - Does the user have no ads?
	 * @param {boolean} context.hasIncognito - Does the user have Incognito?
	 * @param {number} context.joinDate - Date in ms the user joined.
	 * @param {object} context.gatekeeperChecks - Object with gatekeepers checked by SlotMachine.
	 * @returns {string} bannerType
	 */
	static getBannerType(context) {
		// Validate & set the SlotMachine context.
		SlotMachine._validateContext(context);
		SlotMachine._context = context;

		// Find the first banner, by proprity order, that we should show.
		return SlotMachine._bannerPriority
			.find((type) => SlotMachine._shouldShow[type] && SlotMachine._shouldShow[type](context));
	}

	/**
	 * Record a banner being rendered, so we can make our SlotMachine checks accurate.
	 * @param {string} bannerType - which banner was rendered.
	 */
	static recordRender(bannerType) {
		// Increment seen count.
		SlotMachine._setBannerSeenCount(bannerType, SlotMachine._getBannerSeenCount(bannerType) + 1);

		// Record seen date.
		SlotMachine._setBannerLastSeen(bannerType, Date.now());
	}
}

export default SlotMachine;



// WEBPACK FOOTER //
// ./src/shared/util/SlotMachine.js